import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import {
  Breadcrumbs, Container, H2, H3, Section, Spinner,
} from "../components/Primitives";
import { VideoBand } from "../components/VideoBand";
import { Ornament } from "../components/Ornament";
import { getServices } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { LineReveal, Reveal, Stagger, StaggerItem } from "../lib/motion";

const GUARANTEES = [
  ["The Verification Guarantee", "Unconditional", "Every report verifies on the lab's own domain, or your money back."],
  ["The Test-Us Guarantee", "Conditional, self-imposed risk", "Take it to any accredited lab within 30 days; if it doesn't match, full refund plus we pay your lab fee."],
  ["The Loose-First Guarantee", "Conditional", "You inspect before we set anything, cancel free during that window."],
  ["The 72-Hour Guarantee", "Conditional, self-enforcing", "Dispatched in 72 hours or refunded automatically, no request needed."],
  ["The No-Meter Guarantee", "Unconditional", "Flat fee, stated duration, overruns free."],
  ["The Zero-Commission Guarantee", "Structural", "Your consultant earns nothing from what you buy — written into their contract, not just the copy."],
];

export default function ConsultIndex() {
  const { format } = useCurrency();
  const { data, isLoading } = useQuery({ queryKey: ["services"], queryFn: getServices });

  useSEO({
    title: "Consultations — Flat Fee Vedic Astrology & Numerology Online",
    description:
      "Every consultation is a fixed price for a stated length of time. No per-minute meter, no wallet, no balance you can't withdraw. Your consultant earns nothing from what you buy.",
    canonical: "/consult",
    jsonLd: breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Consult", to: "/consult" }]),
  });

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Consult" }]} />
      </Container>

      <VideoBand
        src="/video/jyotish-gold-chart.mp4"
        webm="/video/jyotish-gold-chart.webm"
        poster="/video/jyotish-gold-chart-poster.jpg"
        scrim={0.58}
        surround="#faf7f1"
        testId="consult-band"
        className="border-b border-rule"
      >
        <Container className="py-[110px] text-center max-md:py-[70px]">
          <Reveal>
            <Ornament className="mb-7" />
            <p className="eyebrow-wide">Guidance, at a flat fee</p>
            <h1 className="hero-h1 mx-auto mt-6 max-w-[var(--measure-tight)] !text-[#f3efe7]">
              <LineReveal
                lines={["Guidance,", "at a flat fee"]}
                inViewAmount={0.4}
                stagger={0.12}
                duration={0.9}
              />
            </h1>
            <p className="hero-desc ui mx-auto mt-7 text-[#f3efe7]/85">
              A fixed price for a stated length of time. No per-minute meter, no wallet to top up.
              And the person you speak to earns nothing from what you buy afterwards.
            </p>
            <p className="ui mt-7 text-xs uppercase tracking-[0.28em] text-[#f0d693]/80">
              See The Guarantee Stack behind every session ↓
            </p>
          </Reveal>
        </Container>
      </VideoBand>

      <Section className="border-b border-rule">
        <Container>
          {isLoading ? (
            <Spinner label="Loading consultations" />
          ) : (
            <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" stagger={0.07} data-testid="consult-cards">
              {(data?.items || []).map((s) => (
                <StaggerItem key={s.slug}>
                  <Link
                    to={`/consult/${s.slug}`}
                    data-testid={`consult-card-${s.slug}`}
                    className="card-s elev group flex h-full flex-col p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-gold"
                  >
                    <H3 className="transition-colors group-hover:text-gold">{s.branded_name}</H3>
                    <p className="ui mt-2.5 flex-1 text-sm leading-[1.6] text-dim">{s.seo_subtitle}</p>
                    <div className="mt-6 border-t border-rule pt-5">
                      <p className="num text-2xl text-deep">{format(s.price_inr)}</p>
                      <p className="ui mt-1.5 text-xs text-dim">
                        {s.duration_minutes >= 240
                          ? "Four 60-minute sessions a year"
                          : `${s.duration_minutes} minutes`}
                      </p>
                      <p className="num mt-2.5 text-xs text-verify">
                        Stack value {format(s.stack_value_total)}
                      </p>
                    </div>
                    <span className="btn btn-primary mt-6 w-full">Book</span>
                    <span className="ui mt-3 block text-center text-[11px] leading-snug text-dim">
                      One flat fee — refunded in full if any consultant sells you a product on the call
                    </span>
                  </Link>
                </StaggerItem>
              ))}
              <StaggerItem>
                <div className="card-s elev flex h-full flex-col p-7" data-testid="consult-card-puja">
                  <H3>{data?.puja?.branded_name}</H3>
                  <p className="ui mt-2.5 flex-1 text-sm leading-[1.6] text-dim">{data?.puja?.seo_subtitle}</p>
                  <div className="mt-6 border-t border-rule pt-5">
                    <p className="ui text-sm text-dim">{data?.puja?.headline}</p>
                  </div>
                  <Link to="/consult/puja" className="btn btn-secondary mt-6 w-full">Read more</Link>
                </div>
              </StaggerItem>
            </Stagger>
          )}

          <Reveal delay={0.1}>
            <p className="ui measure mt-10 text-xs leading-[1.7] text-dim">
              Stack values are the sum of each included component priced at what it would cost standalone —
              the written analysis, the recording, follow-up calls, bonuses. They are shown on the card,
              never as a struck-through "was" price against the offer itself. The stack is additive and
              honest, not an inflated anchor.
            </p>
          </Reveal>
        </Container>
      </Section>

      <Section className="border-b border-rule bg-surface">
        <Container className="grid gap-12 lg:grid-cols-3 lg:gap-16">
          <Reveal>
            <H2>What you get with every consultation</H2>
            <ul className="ui mt-6 space-y-3.5 text-sm leading-[1.6] text-dim">
              <li>A written analysis of your chart, sent before the call — so you can see the work was done.</li>
              <li>A named consultant, the same one every time you come back.</li>
              <li>A recording and a written summary, emailed afterwards.</li>
              <li>Named, dated predictions where the tradition supports them, not vague reassurance.</li>
            </ul>
          </Reveal>
          <Reveal delay={0.08}>
            <H2>If the call drops</H2>
            <p className="ui mt-6 text-sm leading-[1.7] text-dim">
              We rebook it free and reverse any charge within 24 hours, automatically. You don't have to
              ask, and you don't have to prove anything. This is The 72-Hour Guarantee's sibling for
              consultations — same principle, different clock.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <H2>What we will never do</H2>
            <p className="ui mt-6 text-sm leading-[1.7] text-dim">
              We will never predict a death, or make predictions about the health or life of a member of
              your family. We will never tell you that something bad will happen if you don't buy
              something. We will never sell you a puja on a call. And we will never advise you to stop,
              delay or change any medical treatment.{" "}
              <Link to="/consult/scope-of-practice" className="link-u">Read our scope of practice →</Link>
            </p>
          </Reveal>
        </Container>
      </Section>

      {/* Palette cleanser — the stack, read as a certificate */}
      <Section className="force-dark">
        <Container>
          <Reveal>
            <p className="eyebrow !text-gold-bright">The Guarantee Stack</p>
            <H2 className="mt-5 max-w-3xl">
              Six of our twelve promises are guarantees in the strict sense
            </H2>
            <p className="ui measure mt-5 text-base leading-[1.7] text-wash/75">
              If we fail, you get your money back, and here is exactly how.
            </p>
          </Reveal>
          <Stagger className="mt-14 grid gap-px bg-rule sm:grid-cols-2 lg:grid-cols-3" stagger={0.08} data-testid="guarantee-stack">
            {GUARANTEES.map(([name, type, text]) => (
              <StaggerItem key={name} className="bg-[#141310] p-8">
                <ShieldCheck className="h-4 w-4 text-verify" aria-hidden="true" />
                <H3 className="mt-4">{name}</H3>
                <p className="ui mt-1.5 text-[11px] uppercase tracking-[0.14em] text-gold-bright">{type}</p>
                <p className="ui mt-4 text-sm leading-[1.7] text-wash/80">{text}</p>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <Link
              to="/promises"
              className="ui group mt-14 inline-flex items-center gap-2 border border-gold-bright px-7 py-3.5 text-sm text-gold-bright transition-colors duration-300 hover:bg-gold-bright hover:text-[#141310]"
              data-testid="consult-all-promises"
            >
              All twelve promises, with the mechanism for holding us to each
            </Link>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
